/**
 * NEXUS UI - DataGrid
 * Futuristic table display with hover effects
 */

import React from 'react';
import type { BaseProps, NexusVariant } from './types';

interface Column<T> {
  key: keyof T | string;
  header: string;
  width?: string;
  align?: 'left' | 'center' | 'right';
  render?: (value: unknown, row: T, index: number) => React.ReactNode;
  mono?: boolean;
  highlight?: boolean;
}

export interface DataGridProps<T extends Record<string, unknown>> extends BaseProps {
  data: T[];
  columns: Column<T>[];
  variant?: NexusVariant;
  striped?: boolean;
  hoverable?: boolean;
  compact?: boolean;
  onRowClick?: (row: T, index: number) => void;
  emptyMessage?: string;
}

const variantColors: Record<NexusVariant, { border: string; highlight: string }> = {
  cyan: { border: 'rgba(0, 212, 255, 0.2)', highlight: '#00d4ff' },
  blue: { border: 'rgba(59, 130, 246, 0.2)', highlight: '#3b82f6' },
  violet: { border: 'rgba(168, 85, 247, 0.2)', highlight: '#a855f7' },
  green: { border: 'rgba(0, 255, 136, 0.2)', highlight: '#00ff88' },
  amber: { border: 'rgba(255, 170, 0, 0.2)', highlight: '#ffaa00' },
  red: { border: 'rgba(255, 51, 102, 0.2)', highlight: '#ff3366' },
};

export function DataGrid<T extends Record<string, unknown>>({
  data,
  columns,
  variant = 'cyan',
  striped = false,
  hoverable = true,
  compact = false,
  onRowClick,
  emptyMessage = 'No data available',
  className = '',
  style,
}: DataGridProps<T>): React.ReactElement {
  const colors = variantColors[variant];
  const padding = compact ? '8px 12px' : '12px 16px';

  const getCellValue = (row: T, key: keyof T | string): unknown => {
    if (typeof key === 'string' && key.includes('.')) {
      return key.split('.').reduce((obj: unknown, k: string) => {
        if (obj && typeof obj === 'object' && k in obj) {
          return (obj as Record<string, unknown>)[k];
        }
        return undefined;
      }, row);
    }
    return row[key as keyof T];
  };

  return (
    <div
      className={`nx-data-grid-wrapper ${className}`}
      style={{
        borderRadius: '4px',
        overflow: 'hidden',
        border: `1px solid ${colors.border}`,
        ...style,
      }}
    >
      <table
        className="nx-data-grid"
        style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontFamily: 'var(--nx-font-interface)',
        }}
      >
        <thead className="nx-data-grid__header">
          <tr style={{ borderBottom: `1px solid ${colors.border}` }}>
            {columns.map((col, i) => (
              <th
                key={`header-${i}`}
                style={{
                  padding,
                  fontSize: '11px',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                  color: 'var(--nx-text-tertiary)',
                  textAlign: col.align || 'left',
                  background: 'var(--nx-void-elevated)',
                  width: col.width,
                  whiteSpace: 'nowrap',
                }}
              >
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.length === 0 ? (
            <tr>
              <td
                colSpan={columns.length}
                style={{
                  padding: '32px 16px',
                  textAlign: 'center',
                  color: 'var(--nx-text-ghost)',
                  fontFamily: 'var(--nx-font-interface)',
                  fontSize: '14px',
                }}
              >
                {emptyMessage}
              </td>
            </tr>
          ) : (
            data.map((row, rowIndex) => (
              <tr
                key={rowIndex}
                className="nx-data-grid__row"
                onClick={() => onRowClick?.(row, rowIndex)}
                style={{
                  borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
                  background: striped && rowIndex % 2 === 1 ? 'rgba(255, 255, 255, 0.02)' : 'transparent',
                  cursor: onRowClick ? 'pointer' : 'default',
                  transition: 'all 0.15s ease-out',
                }}
                onMouseOver={(e) => {
                  if (hoverable) {
                    e.currentTarget.style.background = 'var(--nx-glass-frosted)';
                  }
                }}
                onMouseOut={(e) => {
                  if (hoverable) {
                    e.currentTarget.style.background = striped && rowIndex % 2 === 1 ? 'rgba(255, 255, 255, 0.02)' : 'transparent';
                  }
                }}
              >
                {columns.map((col, colIndex) => {
                  const value = getCellValue(row, col.key);
                  const content = col.render ? col.render(value, row, rowIndex) : String(value ?? '');

                  return (
                    <td
                      key={`cell-${rowIndex}-${colIndex}`}
                      style={{
                        padding,
                        fontSize: compact ? '12px' : '14px',
                        color: col.highlight ? colors.highlight : 'var(--nx-text-secondary)',
                        textAlign: col.align || 'left',
                        fontFamily: col.mono ? 'var(--nx-font-mono)' : 'inherit',
                        verticalAlign: 'middle',
                      }}
                    >
                      {content}
                    </td>
                  );
                })}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

export default DataGrid;
